import { z } from 'zod';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../stored-json-contract.types';
import type {
    IdempotencyCleanupResponse,
    IdempotencyConflictCode,
    IdempotencyConflictResponse,
    IdempotencyEncryptedResultEnvelopeV2,
    IdempotencyHttpResultEnvelopeV1,
    IdempotencyHttpResultHeaderValue,
    IdempotencyResponseBodySnapshot,
    IdempotencyStoredResultEnvelope,
} from '../idempotency.types';
import { JsonValueSchema, NonEmptyStringSchema } from './common.schemas';
import {
    defineSafeIntegerDomain,
    normalizeStoredJsonSafeIntegerObjectFields,
} from '../stored-json-number';

const IdempotencyEncryptedResultEnvelopeStorageVersionSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'IdempotencyEncryptedResultEnvelope.storage_version',
    provenance: {
        kind: 'finite-integer-domain',
        values: [2],
    },
});

export const IdempotencyConflictCodeSchema: z.ZodType<IdempotencyConflictCode> = z.union([
    z.literal('IDEMPOTENCY_REQUEST_IN_PROGRESS'),
    z.literal('IDEMPOTENCY_REQUEST_ALREADY_COMPLETED'),
    z.literal('IDEMPOTENCY_KEY_REUSED_WITH_DIFFERENT_REQUEST'),
    z.literal('IDEMPOTENCY_RESULT_EXPIRED'),
    z.literal('IDEMPOTENCY_RESULT_NOT_REPLAYABLE'),
]);

export const IdempotencyConflictResponseSchema: z.ZodType<IdempotencyConflictResponse> = z.object({
    code: IdempotencyConflictCodeSchema,
    message: NonEmptyStringSchema,
    conflict_resource_type: z.literal('IDEMPOTENCY_REQUEST'),
    conflict_uuid: z.uuid(),
    conflict_field: NonEmptyStringSchema.optional(),
    conflict_value: NonEmptyStringSchema.optional(),
});

export const IdempotencyCleanupResponseSchema: z.ZodType<IdempotencyCleanupResponse> = z.object({
    deleted_count: z.number().int().nonnegative(),
});

export const IdempotencyResponseBodySnapshotSchema: z.ZodType<IdempotencyResponseBodySnapshot> =
    JsonValueSchema;

export const IdempotencyHttpResultHeaderValueSchema: z.ZodType<IdempotencyHttpResultHeaderValue> =
    z.union([z.string(), z.array(z.string())]);

export const IdempotencyHttpResultEnvelopeV1Schema: z.ZodType<IdempotencyHttpResultEnvelopeV1> = z
    .object({
        result_version: z.literal(1),
        status_code: z.number().int().min(100).max(599),
        headers: z.record(z.string().min(1), IdempotencyHttpResultHeaderValueSchema),
        body_base64: z.base64(),
    })
    .strict();

export const IdempotencyEncryptedResultEnvelopeV2Schema: z.ZodType<IdempotencyEncryptedResultEnvelopeV2> =
    z
        .object({
            storage_version: z.literal(2),
            encryption_key_id: z.string().regex(/^[1-9][0-9]*$/),
            encryption_algorithm: NonEmptyStringSchema,
            encrypted_result_base64: z.base64(),
        })
        .strict();

export const StoredIdempotencyEncryptedResultEnvelopeV2Schema: z.ZodType<IdempotencyEncryptedResultEnvelopeV2> =
    z.preprocess(
        (value: unknown): unknown =>
            normalizeStoredJsonSafeIntegerObjectFields(value, {
                safeIntegerDomain:
                    IdempotencyEncryptedResultEnvelopeStorageVersionSafeIntegerDomain,
                fields: ['storage_version'],
            }),
        IdempotencyEncryptedResultEnvelopeV2Schema
    );

export function normalizeIdempotencyResponseBodySnapshotStorageV1(
    _snapshot: IdempotencyResponseBodySnapshot
): IdempotencyStoredResultEnvelope {
    return null;
}

export function normalizeIdempotencyResponseBodySnapshotStorageV2(
    envelope: IdempotencyEncryptedResultEnvelopeV2
): IdempotencyStoredResultEnvelope {
    return envelope;
}

export const IdempotencyResponseBodySnapshotStorageContract = defineStoredJsonContract({
    contractName: 'IdempotencyResponseBodySnapshot',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'IdempotencyResponseBodySnapshotSchema',
            schema: IdempotencyResponseBodySnapshotSchema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['storage_version'],
                },
            ],
            normalize: normalizeIdempotencyResponseBodySnapshotStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'IdempotencyEncryptedResultEnvelopeV2Schema',
            schema: StoredIdempotencyEncryptedResultEnvelopeV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['storage_version'],
                    markerValue: 2,
                },
            ],
            normalize: normalizeIdempotencyResponseBodySnapshotStorageV2,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const StoredIdempotencyResponseBodySnapshotParser: z.ZodType<IdempotencyEncryptedResultEnvelopeV2 | null> =
    buildStoredJsonValueParser(IdempotencyResponseBodySnapshotStorageContract);

export function normalizeIdempotencyHttpResultEnvelopeStorageV1(
    envelope: IdempotencyHttpResultEnvelopeV1
): IdempotencyHttpResultEnvelopeV1 {
    return envelope;
}

export const IdempotencyHttpResultEnvelopeStorageContract = defineStoredJsonContract({
    contractName: 'IdempotencyHttpResultEnvelope',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'IdempotencyHttpResultEnvelopeV1Schema',
            schema: IdempotencyHttpResultEnvelopeV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['result_version'],
                    markerValue: 1,
                },
            ],
            normalize: normalizeIdempotencyHttpResultEnvelopeStorageV1,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const StoredIdempotencyHttpResultEnvelopeParser: z.ZodType<IdempotencyHttpResultEnvelopeV1> =
    buildStoredJsonValueParser(IdempotencyHttpResultEnvelopeStorageContract);
